import axios from 'axios';
import axiosRetry, { exponentialDelay, isNetworkOrIdempotentRequestError } from 'axios-retry';
import { EventSource } from 'eventsource';
import { useOktaAuthContext } from '../contexts/OktaAuthContext.tsx';
// @ts-expect-error qs ships without type declarations (@types/qs is not installed)
import qs from 'qs';
import type {
  ExportCatalogue,
  ExportRequest,
  GeneratedReport,
  Project,
  RainRecommendation,
  Report,
  ReportContext,
  ReportCreate,
  ReportFile,
  ReportPage,
  Solution,
  UserProfile,
} from '../types';
import { parseContentDispositionFilename } from '../utils/reports';

export interface CleaningPackage {
  name?: string;
  [key: string]: unknown;
}

export type CleaningPackages = Record<string, CleaningPackage>;

export interface CostDistribution {
  total: number;
  distribution: Record<string, number>;
}

export type DailyData = {
  times: string[];
  values: Record<string, (number | null)[]>;
  units: Record<string, string>;
};

export interface MitigationSolution
  extends Pick<Solution, 'num_devices' | 'num_cleanings' | 'net_present_value' | 'time_to_clean' | 'cashflow' | 'schedule'> {
  cleaning_time: CostDistribution;
  capex: CostDistribution;
  opex: CostDistribution;
  cleaning_gains: number;
  data: {
    daily: DailyData;
    yearly: { values: Record<string, number[]> };
    monthly: { times: string[]; values: { production_losses: number[]; [key: string]: number[] } };
  };
}

export interface SolutionGroup {
  optimal_cleanings: number;
  optimal_devices: number;
  optimal_index: number;
  solutions: MitigationSolution[];
}

export interface AnalysisResult {
  rain_dates: string[];
  data: { daily: DailyData };
  package_solution_groups: Record<string, SolutionGroup>;
}

export interface SoilingAssessment {
  scores: { global: number; rain: number; deposition: number; soiling_level: number };
  soiling: {
    daily: DailyData;
    monthly: { times: string[]; values: { soiling_ratio: number[] } };
    yearly: { values: { soiling_ratio: number[] } };
  };
}

const URL_BASE = import.meta.env.VITE_BE_BASE_URL + 'soiling-mitigation/';
const genUserProfileURL = () => `${URL_BASE}user-profile`;
const genProjectsURL = () => `${URL_BASE}projects`;
const genProjectURL = (id: string) => `${URL_BASE}projects/${id}`;
const genExportURL = (projectId: string, what: string) => `${genProjectURL(projectId)}/exports/${what}`;
const genReportContextURL = (projectId: string) => `${genProjectURL(projectId)}/report/context`;
const genReportsURL = (projectId: string) => `${genProjectURL(projectId)}/reports`;
const genReportFileURL = (projectId: string, reportId: string) => `${genReportsURL(projectId)}/${encodeURIComponent(reportId)}/file`;
const genSoilingAssessmentURL = () => `${URL_BASE}soiling/assessment`;
const genCleaningPackagesURL = () => `${URL_BASE}cleaning/packages`;
const genMitigationStrategySessionURL = () => `${URL_BASE}mitigation-strategy-session`;
const genMitigationStrategyURL = () => `${URL_BASE}mitigation-strategy`;
const genOperationalStrategySessionURL = () => `${URL_BASE}operational-strategy-session`;
const genOperationalStrategyURL = () => `${URL_BASE}operational-strategy`;
const genOperationalStrategyExPostSessionURL = () => `${URL_BASE}ex-post-analysis-session`;
const genOperationalStrategyExPostURL = () => `${URL_BASE}ex-post-analysis`;
const genScheduleOptimizationSessionURL = () => `${URL_BASE}schedule-optimization-session`;
const genScheduleOptimizationURL = () => `${URL_BASE}schedule-optimization`;
const genCurrentSoilingRatioURL = () => `${URL_BASE}current-soiling-ratio`;
const genValidateCSVURL = () => `${URL_BASE}tools/validate-csv`;
const uploadSoilingSensorDataFILE = () => `${URL_BASE}files`;
const genSensorDataURL = () => `${URL_BASE}sensor-data`;
const genRainRecommendationURL = () => `${URL_BASE}tools/rain-recommendation`;

const progressCallbacks = new Map<string, (progressMessage: string) => void>();

axiosRetry(axios, {
  retries: 3,
  retryDelay: exponentialDelay,
  retryCondition: (error) => isNetworkOrIdempotentRequestError(error) || (error.response?.status ?? 0) >= 500,
});

export const Server = () => {
  const { token } = useOktaAuthContext();

  const registerProgressCallback = (sessionType: string, callback: (progressMessage: string) => void) => {
    progressCallbacks.set(sessionType, callback);
  };

  const unregisterProgressCallback = (sessionType: string) => {
    progressCallbacks.delete(sessionType);
  };

  const getFromServer = async <T,>(url: string): Promise<T> => {
    const response = await axios.get<T>(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const getFromServerWithParams = async <T,>(url: string, params: object): Promise<T> => {
    const response = await axios.get<T>(url, {
      params: params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { indices: false });
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postToServerWithParams = async <T,>(url: string, params: object, body: object): Promise<T> => {
    const response = await axios.post<T>(url, body, {
      params: params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { indices: false });
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postToServer = async <T,>(url: string, payload: object): Promise<T> => {
    const response = await axios.post<T>(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const putToServer = async <T,>(url: string, payload: object): Promise<T> => {
    const response = await axios.put<T>(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const deleteToServer = async <T,>(url: string): Promise<T> => {
    const response = await axios.delete<T>(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postFileToServer = async <T,>(url: string, file: Blob, params: object): Promise<T> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post<T>(url, formData, {
      params: params,
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const getUserProfileAPI = (): Promise<UserProfile> => {
    return getFromServer<UserProfile>(genUserProfileURL());
  };

  const getProjectsAPI = (): Promise<Project[]> => {
    return getFromServer<Project[]>(genProjectsURL());
  };

  const getProjectAPI = (id: string): Promise<Project> => {
    return getFromServer<Project>(genProjectURL(id));
  };

  const postProjectAPI = (payload: object): Promise<string> => {
    return postToServer<string>(genProjectsURL(), payload);
  };

  const copyProjectAPI = (id: string, name: string): Promise<string> => {
    return postToServer<string>(`${genProjectURL(id)}/copy`, { name });
  };

  const putProjectAPI = (id: string, payload: object) => {
    return putToServer(genProjectURL(id), payload);
  };

  const deleteProjectAPI = (id: string) => {
    return deleteToServer(genProjectURL(id));
  };

  const postSoilingAssessmentAPI = (params: { project_id: string }): Promise<SoilingAssessment> => {
    return postToServerWithParams<SoilingAssessment>(genSoilingAssessmentURL(), params, {});
  };

  const getCleaningPackagesAPI = (): Promise<CleaningPackages> => {
    return getFromServer<unknown>(genCleaningPackagesURL()).then((data) => {
      // Recursive function to remove null values
      const removeNullValues = (obj: unknown): unknown => {
        if (obj === null || obj === undefined || (obj as { length?: unknown }).length === 0) {
          return undefined;
        }

        if (Array.isArray(obj)) {
          return obj.map(removeNullValues).filter((item) => item !== undefined);
        }

        if (typeof obj === 'object') {
          const cleaned: Record<string, unknown> = {};
          for (const [key, value] of Object.entries(obj)) {
            const cleanedValue = removeNullValues(value);
            if (cleanedValue !== undefined) {
              cleaned[key] = cleanedValue;
            }
          }
          return Object.keys(cleaned).length > 0 ? cleaned : undefined;
        }

        return obj;
      };

      return removeNullValues(data) as CleaningPackages;
    });
  };

  const postMitigationStrategyAPI = (
    params: { project_id?: string },
    formData: object,
    onCached?: (cached: boolean) => void,
  ): Promise<AnalysisResult> => {
    return new Promise<AnalysisResult>((resolve, reject) => {
      postToServerWithParams<{ session_id: string }>(genMitigationStrategySessionURL(), params, formData)
        .then((sessionResponse) => {
          const sessionId = sessionResponse.session_id;

          const url = new URL(genMitigationStrategyURL());
          url.searchParams.append('session_id', sessionId);

          const eventSource = new EventSource(url.toString(), {
            fetch: (url, init) => {
              return fetch(url, {
                ...init,
                headers: {
                  ...init.headers,
                  Authorization: `Bearer ${token}`,
                },
              });
            },
          });

          // A cache hit streams straight to `result` without any `progress` event.
          let sawProgress = false;

          eventSource.addEventListener('start', (event) => {
            JSON.parse(event.data);
          });

          eventSource.addEventListener('result', (event) => {
            const result: AnalysisResult = JSON.parse(event.data);
            onCached?.(!sawProgress);
            eventSource.close();
            resolve(result);
          });

          eventSource.addEventListener('progress', (event) => {
            sawProgress = true;
            const data = JSON.parse(event.data);
            const progressCallback = progressCallbacks.get('mitigation-strategy');
            if (progressCallback && data.progress !== undefined) {
              progressCallback(data.progress);
            }
          });

          eventSource.addEventListener('error', (event: MessageEvent) => {
            if (!event.data) return; // connection error -> handled by onerror
            const data = JSON.parse(event.data);
            console.error('Analysis error:', data);
            eventSource.close();
            reject(new Error(`Analysis failed: ${data.message}`));
          });

          eventSource.onerror = (event: { code?: number; message?: string; data?: unknown; status?: number }) => {
            if (event?.data) return; // server-sent 'error' event -> handled above
            const status = event?.code ?? event?.status ?? 'n/a';
            const message = event?.message ?? 'connection failed';
            console.error(`EventSource connection error (status ${status}):`, message);
            eventSource.close();
            reject(new Error(`EventSource connection failed (status ${status}): ${message}`));
          };
        })
        .catch((error) => {
          console.error('Failed to create Mitigation strategy session:', error);
          reject(error);
        });
    });
  };

  // Two-step analysis flow: create a session, then stream its events (progress / result / error) over SSE.
  const runAnalysisSession = (
    sessionURL: string,
    sseURL: string,
    progressKey: string,
    params: { project_id: string },
    onCached?: (cached: boolean) => void,
  ): Promise<AnalysisResult> => {
    return new Promise<AnalysisResult>((resolve, reject) => {
      postToServerWithParams<{ session_id: string }>(sessionURL, params, {})
        .then((sessionResponse) => {
          const sessionId = sessionResponse.session_id;

          const url = new URL(sseURL);
          url.searchParams.append('session_id', sessionId);

          const eventSource = new EventSource(url.toString(), {
            fetch: (url, init) => {
              return fetch(url, {
                ...init,
                headers: {
                  ...init.headers,
                  Authorization: `Bearer ${token}`,
                },
              });
            },
          });

          // A cache hit streams straight to `result` without any `progress` event.
          let sawProgress = false;

          eventSource.addEventListener('start', (event) => {
            JSON.parse(event.data);
          });

          eventSource.addEventListener('result', (event) => {
            const result: AnalysisResult = JSON.parse(event.data);
            onCached?.(!sawProgress);
            eventSource.close();
            resolve(result);
          });

          eventSource.addEventListener('progress', (event) => {
            sawProgress = true;
            const data = JSON.parse(event.data);
            const progressCallback = progressCallbacks.get(progressKey);
            if (progressCallback && data.progress !== undefined) {
              progressCallback(data.progress);
            }
          });

          eventSource.addEventListener('error', (event: MessageEvent) => {
            if (!event.data) return; // connection error -> handled by onerror
            const data = JSON.parse(event.data);
            console.error('Analysis error:', data);
            eventSource.close();
            reject(new Error(`Analysis failed: ${data.message}`));
          });

          eventSource.onerror = (event: { code?: number; message?: string; data?: unknown; status?: number }) => {
            if (event?.data) return; // server-sent 'error' event -> handled above
            const status = event?.code ?? event?.status ?? 'n/a';
            const message = event?.message ?? 'connection failed';
            console.error(`EventSource connection error (status ${status}):`, message);
            eventSource.close();
            reject(new Error(`EventSource connection failed (status ${status}): ${message}`));
          };
        })
        .catch((error) => {
          console.error('Failed to create analysis session:', error);
          reject(error);
        });
    });
  };

  const postOperationalStrategyAPI = (params: { project_id: string }, onCached?: (cached: boolean) => void): Promise<AnalysisResult> =>
    runAnalysisSession(genOperationalStrategySessionURL(), genOperationalStrategyURL(), 'operational-strategy', params, onCached);

  const postOperationalStrategyExPostAPI = (
    params: { project_id: string },
    onCached?: (cached: boolean) => void,
  ): Promise<AnalysisResult> =>
    runAnalysisSession(genOperationalStrategyExPostSessionURL(), genOperationalStrategyExPostURL(), 'ex-post', params, onCached);

  const postScheduleOptimizationAPI = (params: { project_id: string }, onCached?: (cached: boolean) => void): Promise<AnalysisResult> =>
    runAnalysisSession(genScheduleOptimizationSessionURL(), genScheduleOptimizationURL(), 'schedule-optimization', params, onCached);

  const getCurrentSoilingRatioAPI = (params: { project_id: string }): Promise<{ current_soiling_ratio: number | null }> =>
    getFromServerWithParams(genCurrentSoilingRatioURL(), params);

  const postValidateCSVAPI = (file: Blob, params: object): Promise<{ warning?: string; [key: string]: unknown }> => {
    return postFileToServer(genValidateCSVURL(), file, params);
  };

  const postSoilingSensorDataFileAPI = (file: Blob, params: object): Promise<string | { file_uid?: string; warning?: string }> => {
    return postFileToServer(uploadSoilingSensorDataFILE(), file, params);
  };

  const postSensorDataAPI = (params: { file_uid: string; file_type: string; project_id: string }) => {
    return postToServerWithParams(genSensorDataURL(), params, {});
  };

  const getRainRecommendationAPI = (params: { latitude: number; longitude: number }): Promise<RainRecommendation> => {
    return getFromServerWithParams(genRainRecommendationURL(), params);
  };

  const getReportContextAPI = async (
    projectId: string,
    params: { includeResults: boolean },
    signal?: AbortSignal,
  ): Promise<ReportContext> => {
    const response = await axios.get<ReportContext>(genReportContextURL(projectId), {
      params: { include_results: params.includeResults },
      signal,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  };

  // Generation is not idempotent (each call archives a new version): never retried automatically.
  const createReportAPI = async (projectId: string, body: ReportCreate): Promise<GeneratedReport> => {
    const response = await axios.post<GeneratedReport>(genReportsURL(projectId), body, {
      'axios-retry': { retries: 0 },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const getReportsAPI = async (projectId: string, params: { limit: number; skip: number }): Promise<ReportPage> => {
    const response = await axios.get<Report[]>(genReportsURL(projectId), {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    const total = Number(response.headers['x-total-count']);
    return { items: response.data, total: Number.isFinite(total) ? total : params.skip + response.data.length };
  };

  const downloadReportAPI = async (projectId: string, reportId: string): Promise<ReportFile> => {
    const response = await axios.get<Blob>(genReportFileURL(projectId, reportId), {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      blob: response.data,
      filename: parseContentDispositionFilename(response.headers['content-disposition']),
    };
  };

  const getExportTablesAPI = async (projectId: string, signal?: AbortSignal): Promise<ExportCatalogue> => {
    const response = await axios.get<ExportCatalogue>(genExportURL(projectId, 'tables'), {
      signal,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  };

  const downloadExportAPI = async (projectId: string, request: ExportRequest): Promise<ReportFile> => {
    // FastAPI reads a repeated parameter (`table=a&table=b`), not axios' default `table[]=a`.
    const query = request.format === 'csv' ? '?' + request.tables.map((table) => `table=${encodeURIComponent(table)}`).join('&') : '';
    const response = await axios.get<Blob>(genExportURL(projectId, request.format) + query, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      blob: response.data,
      filename: parseContentDispositionFilename(response.headers['content-disposition']),
    };
  };

  return {
    URL_BASE,
    getFromServerWithParams,
    getUserProfileAPI,
    getProjectsAPI,
    getProjectAPI,
    postProjectAPI,
    putProjectAPI,
    copyProjectAPI,
    deleteProjectAPI,
    postSoilingAssessmentAPI,
    getCleaningPackagesAPI,
    postMitigationStrategyAPI,
    postOperationalStrategyAPI,
    postOperationalStrategyExPostAPI,
    postScheduleOptimizationAPI,
    getCurrentSoilingRatioAPI,
    postValidateCSVAPI,
    postSoilingSensorDataFileAPI,
    postSensorDataAPI,
    getRainRecommendationAPI,
    getExportTablesAPI,
    downloadExportAPI,
    getReportContextAPI,
    createReportAPI,
    getReportsAPI,
    downloadReportAPI,
    postToServerWithParams,
    registerProgressCallback,
    unregisterProgressCallback,
  };
};
